import { AutoCoderPipeline, PipelineLifecycle } from '../types/gotypes'

/**
 * The two independent gates on a pipeline (C11):
 *   - connector — may a data-collection loop add new records?    (fill)
 *   - coding    — may the dispatcher pull batches off the lanes
 *                 and pay for them?                              (drain)
 *
 * There used to be a third, batching — may records be sealed into coding
 * batches and put in the queue lanes? T19 (2026-08-12) retired it: opening a
 * coding row and creating its batch are now one atomic write, so there is no
 * gated moment between them for a switch to guard.
 *
 * This MUST mirror the Go derivation in goapi/pkg/types/autocoder.go
 * (`ConnectorImportsEnabled` / `CodingRunsEnabled`) exactly, because the flags
 * live in the pipeline's config jsonb and are absent on every pipeline nobody
 * has ever toggled — absent means "derive from lifecycle", not "off".
 */
export interface PipelineSwitchState {
  connectorEnabled: boolean
  codingEnabled: boolean
}

// Pre-C11 import predicate: only `active` pipelines were ever fetched for.
const defaultConnectorEnabled = (lifecycle?: PipelineLifecycle): boolean =>
  lifecycle === PipelineLifecycle.active

// Pre-C11 coding predicate: Ingest skipped batch creation for `paused` and
// nothing else, so one_shot / archived / unset all keep coding.
const defaultCodingEnabled = (lifecycle?: PipelineLifecycle): boolean =>
  lifecycle !== PipelineLifecycle.paused

export const pipelineSwitchState = (pipeline: AutoCoderPipeline): PipelineSwitchState => {
  const lifecycle = pipeline?.lifecycle
  const config = pipeline?.config

  // Archived overrides the connector switch: the server refuses to ingest into
  // an archived pipeline, so an armed connector could only burn upstream calls.
  const connectorEnabled = lifecycle === PipelineLifecycle.archived
    ? false
    : config?.connector_enabled ?? defaultConnectorEnabled(lifecycle)

  // Coding has no lifecycle override — an explicit flag always wins (archived
  // pipelines coded before C11/E2 and keep doing so unless a switch says
  // otherwise).
  const codingEnabled = config?.coding_enabled ?? defaultCodingEnabled(lifecycle)

  return { connectorEnabled, codingEnabled }
}

/**
 * Only long-lived pipelines get the switches. A one-shot pipeline (or a legacy
 * row with no lifecycle at all, which the backend treats as one-shot) has no
 * connector loop and no ongoing spend to gate, so it shows neither toggle.
 */
export const showsPipelineSwitches = (pipeline: AutoCoderPipeline): boolean => {
  const lifecycle = pipeline?.lifecycle
  if (!lifecycle) return false
  return lifecycle !== PipelineLifecycle.one_shot
}

/**
 * The connector chip additionally hides on archived pipelines: the server
 * refuses to ingest into an archived pipeline, so switching importing back ON
 * is a no-op that would still return 200 and report success. Rather than
 * showing a control whose ON direction can never take effect, don't show it.
 *
 * NOTE: no component renders an Importing switch today — PipelineGateSwitches
 * shows the pipeline's own gates only (see the note there). This is kept, with
 * its tests, for the feeds/channels view the control is expected to reappear
 * on: the rule it encodes ("archived can never import") does not change, and
 * the flag itself is still live in the API and in the janitor.
 */
export const showsConnectorSwitch = (pipeline: AutoCoderPipeline): boolean =>
  showsPipelineSwitches(pipeline) && pipeline?.lifecycle !== PipelineLifecycle.archived

/** The two gates, as the UI names them. */
export type PipelineGateName = 'connector' | 'coding'

export interface PipelineSwitchAvailability {
  disabled: boolean
  /** Why the switch is inert. Present only when `disabled`. */
  note?: string
  /** The switch works, but the state it is in needs saying out loud. */
  warning?: string
}

/**
 * Whether a given gate's switch may be operated — asked PER GATE, because the
 * gates have genuinely different backends.
 *
 * There used to be a third case here, BATCHING, disabled on a paused or
 * archived pipeline because the unattended sealer required an explicit opt-in
 * plus `lifecycle === active`. T19 (2026-08-12) retired both the switch and
 * the sealer that read it.
 *
 * CODING is never disabled. Go gates dispatch on the coding flag ALONE
 * (`pendingBatchCodingEnabledClause`): there is no lifecycle predicate. A
 * PAUSED pipeline carrying an explicit `coding_enabled: true` — precisely what
 * the backfill runbook creates — keeps dispatching and keeps billing, and an
 * ARCHIVED pipeline whose key was never written derives ON and does the same.
 * Disabling this switch there removed the operator's only way to stop the spend
 * (archived pipelines cannot even be paused from the UI). So Coding is never
 * disabled; where the pipeline's state and its spend disagree, we say so.
 *
 * CONNECTOR is refused server-side only for archived pipelines, and archived
 * hides the switch entirely (`showsConnectorSwitch`) — so wherever it is shown
 * it is operable.
 */
export const pipelineSwitchAvailability = (
  pipeline: AutoCoderPipeline,
  gate: PipelineGateName,
): PipelineSwitchAvailability => {
  const lifecycle = pipeline?.lifecycle

  if (gate === 'coding') {
    const stillSpending = pipelineSwitchState(pipeline).codingEnabled
    if (stillSpending && lifecycle === PipelineLifecycle.paused) {
      return {
        disabled: false,
        warning:
          'The pipeline is paused, but submitted batches are still being coded and billed — switch Coding off to stop the spend.',
      }
    }
    if (stillSpending && lifecycle === PipelineLifecycle.archived) {
      return {
        disabled: false,
        warning:
          'The pipeline is archived, but submitted batches are still being coded and billed — switch Coding off to stop the spend.',
      }
    }
    return { disabled: false }
  }

  return { disabled: false }
}
